import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ApolloError, useMutation } from "@apollo/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";

import {
  PageContainer,
  PageContent,
  PageError,
  PageLoading,
} from "apps/user/learner/components/page-container";
import {
  AdventCampaign,
  AdventDoor,
  OpenAdventDoorMutation,
} from "apps/user/learner/api/types";
import useAdventCampaign from "apps/user/learner/hooks/use-advent-campaign";
import useCompleteAdventDoor from "apps/user/learner/hooks/use-complete-advent-door";
import useWebOriEnabled from "apps/user/learner/hooks/use-web-ori-enabled";
import {
  analyticsValue,
  claimFirstOpen,
  ContentDestination,
  ContentTarget,
  daysToStart,
  doorByDay,
  doorState,
  formatDoorDate,
  hasSeenTour,
  isAfterRun,
  isBeforeStart,
  isDoorForToday,
  isDoorUnlocked,
  localToday,
  markTourSeen,
  prefersReducedMotion,
  scatterDoors,
  todaysDoor,
  unopenedUnlockedCount,
} from "apps/user/learner/lib/advent-doors";
import { DoorMechanicResponse } from "apps/user/learner/lib/advent-door-mechanics";
import {
  setOriStarterMessage,
  wasStarterSentRecently,
} from "apps/user/learner/lib/ori-starter-message";
import { useToast } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import { useOpenModalFromCard } from "providers/content-modal-manager";
import { logError } from "utility/Function";
import { OPEN_ADVENT_DOOR } from "utility/graphQl/mutation";
import { trackEvent } from "utility/mixpanel";

import DoorModal from "./components/DoorModal";
import NightSky from "./components/NightSky";
import DoorsProgress from "./components/DoorsProgress";
import DoorTile from "./components/DoorTile";
import WelcomeTour from "./components/WelcomeTour";
import styles from "./december-doors.module.scss";

const NIGHT_BODY_CLASS = "december-doors-night";

const ORI_ENTRY_POINT = "december_door";
const DOOR_LOCKED_CODE = "DOOR_LOCKED";
const DOOR_PARAM = "door";
// The sheet rises part-way through the hinge so the reveal reads first.
const MODAL_DELAY_MS = 300;
const SPARKS_MS = 700;
const BLOOM_MS = 900;

const errorCode = (error: unknown): string | undefined => {
  if (!(error instanceof ApolloError)) return undefined;
  const code = error.graphQLErrors[0]?.extensions?.code;
  return typeof code === "string" ? code : undefined;
};

const DecemberDoorsPage = () => {
  const { t, i18n } = useTranslation(["decemberDoors"]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openToast } = useToast();
  const openModalFromCard = useOpenModalFromCard();
  const { enabled, flagsResolved, campaign, mechanics, loading, error } =
    useAdventCampaign();
  const completeDoor = useCompleteAdventDoor();
  const { enabled: isOriEnabled } = useWebOriEnabled();
  const [openDoor] = useMutation<OpenAdventDoorMutation>(OPEN_ADVENT_DOOR);
  const [selectedDoorId, setSelectedDoorId] = useState<string | null>(null);
  const [openingDoorId, setOpeningDoorId] = useState<string | null>(null);
  const [sparklingDoorId, setSparklingDoorId] = useState<string | null>(null);
  const [isBlooming, setIsBlooming] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);
  // The home band lands here with `?door=N`; it is honoured once, then
  // dropped from the URL so a refresh does not open the door again.
  const [deepLinkDay] = useState<number | null>(() => {
    const day = searchParams.get(DOOR_PARAM);
    return day === null ? null : Number(day);
  });
  const [isDeepLinkHandled, setIsDeepLinkHandled] = useState(
    deepLinkDay === null
  );
  const gridRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<number[]>([]);

  const later = useCallback((callback: () => void, delay: number) => {
    timersRef.current.push(window.setTimeout(callback, delay));
  }, []);

  useEffect(
    () => () => timersRef.current.forEach((timer) => clearTimeout(timer)),
    []
  );

  // Paint the document midnight while the calendar is mounted, so scrolling
  // past either end shows night rather than the app's white.
  useEffect(() => {
    document.body.classList.add(NIGHT_BODY_CLASS);
    return () => document.body.classList.remove(NIGHT_BODY_CLASS);
  }, []);

  // The route is reachable by URL with the flag off: send those learners
  // home once the flags are known, and only then (an unresolved flag is
  // not a "no").
  useEffect(() => {
    if (flagsResolved && !enabled) navigate("/user/home", { replace: true });
  }, [flagsResolved, enabled, navigate]);

  const today = localToday();
  const campaignId = campaign?.id;
  const campaignSlug = campaign?.slug;
  // A week ahead of the first door the calendar counts down: every door is
  // shut, so there is nothing to open from the hero or a link, and the
  // welcome tour waits for door 1 rather than announce a door that is not
  // there.
  const isCountdown = campaign ? isBeforeStart(campaign, today) : false;

  // Today's row sits first under the hero, unless it already is the first.
  useEffect(() => {
    if (!campaignId) return;
    const grid = gridRef.current;
    if (!grid) return;
    const todayTile = grid.querySelector<HTMLElement>("[data-today]");
    const firstTile = grid.firstElementChild as HTMLElement | null;
    if (!todayTile || !firstTile) return;
    if (todayTile.offsetTop === firstTile.offsetTop) return;
    todayTile.scrollIntoView({
      block: "start",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [campaignId]);

  useEffect(() => {
    if (!campaignSlug || isCountdown || hasSeenTour(campaignSlug)) return;
    setIsTourOpen(true);
  }, [campaignSlug, isCountdown]);

  // A real advent calendar scatters its numbers; the arrangement is fixed
  // per campaign so it never moves between visits.
  const scatteredDoors = useMemo(
    () => (campaign ? scatterDoors(campaign.doors, campaign.slug) : []),
    [campaign]
  );

  const handleOpen = useCallback(
    async (door: AdventDoor) => {
      if (openingDoorId !== null) return;
      if (door.isOpened) {
        setSelectedDoorId(door.id);
        return;
      }
      setOpeningDoorId(door.id);
      const startedAt = Date.now();
      try {
        await openDoor({ variables: { doorId: door.id } });
        trackEvent("Advent Door Opened", {
          Campaign: campaign?.slug,
          "Door Day": door.dayNumber,
          "Door Type": analyticsValue(door.doorType),
          Category: analyticsValue(door.category),
        });
        const isFirstOpen = claimFirstOpen();
        if (prefersReducedMotion()) {
          setSelectedDoorId(door.id);
          return;
        }
        if (isFirstOpen) {
          setIsBlooming(true);
          later(() => setIsBlooming(false), BLOOM_MS);
        }
        setSparklingDoorId(door.id);
        later(() => setSparklingDoorId(null), SPARKS_MS);
        later(
          () => setSelectedDoorId(door.id),
          Math.max(0, MODAL_DELAY_MS - (Date.now() - startedAt))
        );
      } catch (err) {
        logError("Failed to open advent door", err);
        const isLocked = errorCode(err) === DOOR_LOCKED_CODE;
        openToast({
          messageHeader: isLocked
            ? t("decemberDoors:locked.title")
            : t("decemberDoors:error.title"),
          message: isLocked
            ? t("decemberDoors:locked.hint", {
                date: formatDoorDate(door.date, i18n.language),
              })
            : t("decemberDoors:error.body"),
          className: styles.toast,
          size: "medium",
        });
      } finally {
        setOpeningDoorId(null);
      }
    },
    [
      openingDoorId,
      openDoor,
      campaign?.slug,
      later,
      openToast,
      t,
      i18n.language,
    ]
  );

  useEffect(() => {
    if (isDeepLinkHandled || !campaign || deepLinkDay === null) return;
    setIsDeepLinkHandled(true);
    setSearchParams({}, { replace: true });
    if (isCountdown) return;
    // A first visit sees the welcome tour first; its last step opens the
    // door the link asked for.
    if (!hasSeenTour(campaign.slug)) return;
    const door = doorByDay(campaign.doors, deepLinkDay);
    if (door) handleOpen(door);
  }, [
    isDeepLinkHandled,
    campaign,
    deepLinkDay,
    isCountdown,
    setSearchParams,
    handleOpen,
  ]);

  const handleComplete = useCallback(
    async (door: AdventDoor, response: DoorMechanicResponse | null) => {
      const isFirstCompletion = !door.isCompleted;
      await completeDoor(door.id, response);
      if (!isFirstCompletion) return;
      trackEvent("Advent Door Completed", {
        Campaign: campaignSlug,
        "Door Day": door.dayNumber,
        "Door Type": analyticsValue(door.doorType),
        Category: analyticsValue(door.category),
        Mechanic: analyticsValue(door.mechanic),
      });
    },
    [completeDoor, campaignSlug]
  );

  const trackCta = (door: AdventDoor, destination: string) => {
    trackEvent("Advent Door CTA Tapped", {
      "Door Day": door.dayNumber,
      "Door Type": analyticsValue(door.doorType),
      Destination: destination,
    });
  };

  const handleOpenContent = (
    door: AdventDoor,
    destination: ContentDestination
  ) => {
    trackCta(door, destination.target);
    setSelectedDoorId(null);
    if (destination.target === ContentTarget.Article) {
      navigate(`/user/articles/${destination.id}`);
      return;
    }
    openModalFromCard({ type: destination.target, id: destination.id });
  };

  const handleChatToOri = (door: AdventDoor) => {
    trackCta(door, "ori");
    const prompt = door.oriPrompt ?? "";
    if (!wasStarterSentRecently(prompt)) {
      setOriStarterMessage(prompt, ORI_ENTRY_POINT);
    }
    navigate("/user/chat");
  };

  const dismissTour = () => {
    if (campaignSlug) markTourSeen(campaignSlug);
    setIsTourOpen(false);
  };

  const renderState = (content: React.ReactNode) => (
    <PageContainer background="transparent" className={styles.night}>
      <NightSky />
      <PageContent maxWidth="lg">
        <div className={styles.page}>{content}</div>
      </PageContent>
    </PageContainer>
  );

  if (!flagsResolved) {
    return renderState(<PageLoading />);
  }
  if (!enabled) {
    return null;
  }
  if (error) {
    return renderState(<PageError />);
  }
  if (loading && !campaign) {
    return renderState(<PageLoading />);
  }
  if (!campaign) {
    return renderState(
      <div className={styles.empty} data-testid="december-doors-empty">
        <h1 className={styles["empty-title"]}>
          {t("decemberDoors:empty.title")}
        </h1>
      </div>
    );
  }

  const doorToday = todaysDoor(campaign.doors, today);
  // The door the tour's last step opens: the one the link asked for while
  // it is open to the learner, otherwise today's.
  const linkedDoor =
    deepLinkDay === null ? undefined : doorByDay(campaign.doors, deepLinkDay);
  const tourDoor =
    linkedDoor && isDoorUnlocked(linkedDoor, today) ? linkedDoor : doorToday;
  const readyDay = tourDoor && !tourDoor.isOpened ? tourDoor.dayNumber : null;
  const selectedDoor =
    campaign.doors.find((door) => door.id === selectedDoorId) ?? null;

  const renderTodayLine = (current: AdventCampaign) => {
    if (isCountdown) {
      return (
        <p className={styles["hero-line"]} data-testid="hero-line">
          {t("decemberDoors:empty.beforeStart", {
            date: formatDoorDate(current.startDate, i18n.language),
          })}
          <span className={styles["hero-line-detail"]}>
            {t("decemberDoors:empty.daysToGo", {
              count: daysToStart(current, today),
            })}
          </span>
        </p>
      );
    }
    if (doorToday && !doorToday.isOpened) {
      return (
        <div className={styles["hero-line"]} data-testid="hero-line">
          <span>
            {t("decemberDoors:hero.today", { day: doorToday.dayNumber })}
          </span>
          <button
            type="button"
            className={styles["gold-button"]}
            onClick={() => handleOpen(doorToday)}
            data-testid="hero-open-today"
          >
            {t("decemberDoors:hero.openToday", { day: doorToday.dayNumber })}
          </button>
        </div>
      );
    }
    const isEveryDoorOpen = current.openedCount >= current.totalDoors;
    if (isEveryDoorOpen) {
      return (
        <p className={styles["hero-line"]} data-testid="hero-line">
          {t("decemberDoors:hero.allOpen")}
        </p>
      );
    }
    if (doorToday) {
      const tomorrow = doorByDay(current.doors, doorToday.dayNumber + 1);
      if (!tomorrow) return null;
      return (
        <p className={styles["hero-line"]} data-testid="hero-line">
          {t("decemberDoors:hero.tomorrow", { day: tomorrow.dayNumber })}
        </p>
      );
    }
    if (isAfterRun(current, today)) {
      return (
        <p className={styles["hero-line"]} data-testid="hero-line">
          {t("decemberDoors:empty.afterRun", {
            date: formatDoorDate(current.endDate, i18n.language),
          })}
        </p>
      );
    }
    return null;
  };

  return (
    <PageContainer background="transparent" className={styles.night}>
      <NightSky />
      <PageContent maxWidth="lg">
        <div className={styles.page} data-testid="december-doors-page">
          <header
            className={clsx(styles.hero, isBlooming && styles["hero-bloom"])}
          >
            <h1 className={styles["hero-title"]}>{campaign.introTitle}</h1>
            <p className={styles["hero-body"]}>{campaign.introBody}</p>
            {renderTodayLine(campaign)}
            {!isCountdown && (
              <DoorsProgress
                opened={campaign.openedCount}
                total={campaign.totalDoors}
                stillToOpen={unopenedUnlockedCount(campaign.doors, today)}
              />
            )}
          </header>
          <div className={styles.grid} ref={gridRef}>
            {scatteredDoors.map((door) => (
              <DoorTile
                key={door.id}
                door={door}
                state={doorState(door, today)}
                isToday={isDoorForToday(door, today)}
                isOpening={openingDoorId === door.id}
                isSparkling={sparklingDoorId === door.id}
                onOpen={handleOpen}
              />
            ))}
          </div>
        </div>
        <DoorModal
          door={selectedDoor}
          mechanic={
            selectedDoor ? mechanics.get(selectedDoor.id) ?? null : null
          }
          doors={campaign.doors}
          isOriEnabled={isOriEnabled}
          onClose={() => setSelectedDoorId(null)}
          onOpenContent={handleOpenContent}
          onChatToOri={handleChatToOri}
          onComplete={handleComplete}
        />
        <WelcomeTour
          isOpen={isTourOpen}
          todayDay={readyDay}
          isReducedMotion={prefersReducedMotion()}
          onDismiss={dismissTour}
          onStart={() => {
            dismissTour();
            if (tourDoor) handleOpen(tourDoor);
          }}
        />
      </PageContent>
    </PageContainer>
  );
};

export default DecemberDoorsPage;
